import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import ReactECharts from "echarts-for-react";
import {
  Target, Users, DollarSign, ArrowLeft, TrendingUp, AlertTriangle, CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const PGMOResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/pgmo/resources`, { headers });
        setResources(res.data.resources || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const overAllocated = resources.filter(r => r.over_allocated).length;
  const available = resources.filter(r => r.availability > 20).length;
  const avgUtilization = resources.length ? (resources.reduce((sum, r) => sum + r.total_allocation, 0) / resources.length).toFixed(0) : 0;

  const utilizationChartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: { type: 'value', max: 150, axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8', formatter: '{value}%' } },
    yAxis: { type: 'category', data: resources.slice(0, 8).map(r => r.employee_name.split(' ')[0]), axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8' } },
    series: [{
      type: 'bar',
      data: resources.slice(0, 8).map(r => ({
        value: r.total_allocation,
        itemStyle: { color: r.over_allocated ? '#ef4444' : r.total_allocation > 80 ? '#eab308' : '#22c55e' }
      })),
      label: { show: true, position: 'right', color: '#94a3b8', formatter: '{c}%' }
    }],
    markLine: { data: [{ xAxis: 100, lineStyle: { color: '#ef4444', type: 'dashed' }, label: { formatter: 'Capacity', color: '#ef4444' } }] }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Resource Allocation...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="pgmo-resources-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin" className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Resource Allocation
          </h1>
          <p className="text-slate-400 text-sm">PGMO staff allocation and capacity planning</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{resources.length}</p>
            <p className="text-xs text-slate-400">Total Staff</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <TrendingUp className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{avgUtilization}%</p>
            <p className="text-xs text-slate-400">Avg Utilization</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{overAllocated}</p>
            <p className="text-xs text-slate-400">Over-Allocated</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{available}</p>
            <p className="text-xs text-slate-400">Available Capacity</p>
          </CardContent>
        </Card>
      </div>

      {/* Over-allocation Alert */}
      {overAllocated > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm mb-6">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{overAllocated} staff members are over-allocated (&gt;100%) - rebalancing recommended</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Utilization Chart */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Staff Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={utilizationChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Allocation Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Under-utilized (&lt;50%)</span>
                <span className="text-green-400">{resources.filter(r => r.total_allocation < 50).length} staff</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Optimal (50-80%)</span>
                <span className="text-blue-400">{resources.filter(r => r.total_allocation >= 50 && r.total_allocation <= 80).length} staff</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">High utilization (80-100%)</span>
                <span className="text-yellow-400">{resources.filter(r => r.total_allocation > 80 && r.total_allocation <= 100).length} staff</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Over-allocated (&gt;100%)</span>
                <span className="text-red-400">{resources.filter(r => r.total_allocation > 100).length} staff</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-700">
              <p className="text-slate-400 text-sm mb-2">Department Breakdown</p>
              {["Engineering", "PMO", "Finance", "Operations"].map(dept => {
                const deptStaff = resources.filter(r => r.department === dept);
                const deptAvg = deptStaff.length ? Math.round(deptStaff.reduce((s, r) => s + r.total_allocation, 0) / deptStaff.length) : 0;
                return (
                  <div key={dept} className="flex items-center justify-between py-1">
                    <span className="text-white text-sm">{dept}</span>
                    <div className="flex items-center gap-2">
                      <Progress value={deptAvg} className="w-20 h-2" />
                      <span className="text-slate-400 text-sm w-12 text-right">{deptAvg}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resource Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Staff Allocation Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Projects</th>
                  <th>Allocation</th>
                  <th>Availability</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((resource) => (
                  <tr key={resource.employee_id}>
                    <td className="text-white font-medium">{resource.employee_name}</td>
                    <td className="text-slate-300">{resource.department}</td>
                    <td className="text-slate-300 capitalize">{resource.role?.replace("_", " ")}</td>
                    <td className="text-blue-400">{resource.projects_assigned}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={Math.min(resource.total_allocation, 100)} 
                          className={`w-20 h-2 ${resource.over_allocated ? '[&>div]:bg-red-500' : ''}`} 
                        />
                        <span className={resource.over_allocated ? "text-red-400" : "text-slate-400"}>
                          {resource.total_allocation}%
                        </span>
                      </div>
                    </td>
                    <td className={resource.availability > 20 ? "text-green-400" : resource.availability > 0 ? "text-yellow-400" : "text-red-400"}>
                      {resource.availability}%
                    </td>
                    <td>
                      {resource.over_allocated ? (
                        <Badge className="bg-red-500/20 text-red-400">Over-allocated</Badge>
                      ) : resource.status === "on_leave" ? (
                        <Badge className="bg-yellow-500/20 text-yellow-400">On Leave</Badge>
                      ) : (
                        <Badge className="bg-green-500/20 text-green-400">Available</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PGMOResourcesPage;
